import React from "react";
import FetchHelper from "./lib/fetchHelper";
import { BPSwapOfferStatus, swapOfferStatusText } from "./lib/bpSwapOfferStatus";
import { ReactRenderer } from "./lib/ReactRenderer";
import { SWAP_CHANGED_EVENT, possessive, roleName, shiftWhen } from "./lib/swapShiftLine";
/**
 * "Mine byttetilbud" - the swap offers this volunteer has sent to other people, which is the only
 * place besides the SMS where a rejected or lapsed offer explains itself.
 *
 * Its own card, separate from the byttevagter they have received offers on: the two are opposite
 * ends of the loop and only coincide on this page. They stay in step through
 * SWAP_CHANGED_EVENT, since accepting a swap over in the other card can lapse an offer listed here.
 */
export default class MyShiftOffersComponent extends React.Component {
    constructor(props) {
        super(props);
        this.load = () => FetchHelper.get(`/bpapi/shiftswap/mine?includeResolved=${this.state.showResolved}`)
            .then((result) => this.setState({
            isLoaded: true,
            busy: false,
            memberUdi: result.memberUdi,
            offers: result.items
        }))
            .catch(() => this.setState({ isLoaded: true, busy: false, error: "Kunne ikke hente dine byttetilbud" }));
        /**
         * Takes one offered shift back. The offer holding it is withdrawn too, but only if that was the
         * last shift it named - which the volunteer never sees, since the page is arranged by shift.
         */
        this.withdrawShift = (offerId, shiftMemberId) => {
            this.setState({ busy: true, error: null });
            return FetchHelper.del(`/bpapi/shiftswap/${offerId}/shift/${shiftMemberId}`)
                .then(() => {
                this.load();
                // The owner's inbox in the other card loses this shift, so tell it to re-fetch.
                window.dispatchEvent(new CustomEvent(SWAP_CHANGED_EVENT));
            })
                // A withdrawal only fails because the offer stopped being pending, and the likeliest
                // reason is that the owner accepted it while this page sat open. Say so rather than
                // inviting a retry of something the rosters have already moved past. The owner's name
                // comes from the re-read rather than from the row that was clicked, which is arranged
                // by shift and no longer carries the byttevagt.
                .catch(() => FetchHelper.get(`/bpapi/shiftswap/mine?includeResolved=true`)
                .then((result) => {
                var _a;
                const resolved = result.items.find((o) => o.id === offerId);
                this.setState({
                    busy: false,
                    error: (resolved === null || resolved === void 0 ? void 0 : resolved.status) === BPSwapOfferStatus.accepted
                        ? `${(_a = resolved.shiftExchange) === null || _a === void 0 ? void 0 : _a.offeredByName} har allerede accepteret et af dine bytteforslag. Byttet er gennemført og kan ikke fortrydes.`
                        : "Vagten kunne ikke trækkes tilbage"
                });
            })
                .catch(() => this.setState({ busy: false, error: "Vagten kunne ikke trækkes tilbage" }))
                .then(() => this.load()));
        };
        this.toggleResolved = () => this.setState({ showResolved: !this.state.showResolved }, () => this.load());
        /**
         * The offers grouped by the byttevagt they answer, so this card reads the same way as *Mine
         * byttevagter*: whose shift it is in the header, what is on the table in the body. A volunteer
         * can have several offers standing on one byttevagt, and listing them flat repeated that shift
         * once per offer while saying nothing about them belonging together.
         */
        this.groupByExchange = (offers) => {
            const groups = [];
            offers.forEach((offer) => {
                var _a, _b;
                const id = (_b = (_a = offer.shiftExchange) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : 0;
                const group = groups.find((g) => g.id === id);
                if (group)
                    group.offers.push(offer);
                else
                    groups.push({ id, shiftExchange: offer.shiftExchange, offers: [offer] });
            });
            return groups;
        };
        /**
         * The shifts offered on one byttevagt, gathered by what has become of them rather than by the
         * offer row that happens to hold each one.
         *
         * Sending two proposals to the same person on two evenings is one conversation, not two: the
         * split into one block per offer row showed the same byttevagt answered twice under identical
         * headings, which reads as a mistake. Whether a shift travelled in its own submission or
         * alongside another is bookkeeping the volunteer never asked about.
         */
        this.byStatus = (offers) => {
            const blocks = [];
            offers.forEach((offer) => {
                const rows = offer.candidates.map((c) => (Object.assign(Object.assign({}, c), { offerId: offer.id })));
                const block = blocks.find((b) => b.status === offer.status);
                if (block)
                    block.rows.push(...rows);
                else
                    blocks.push({ status: offer.status, rows });
            });
            return blocks;
        };
        /**
         * Each offered shift keeps its own withdraw control, because that is the grain the volunteer
         * thinks in: the cap is spent in shifts and the owner chooses between shifts, so offering three
         * and regretting one must not mean taking all three off the table. The server withdraws the
         * offer holding a shift only when the last of its shifts goes, which is invisible from here -
         * as it should be.
         */
        this.renderStatusBlock = (block, groupId) => (React.createElement("div", { className: "bi-mb-offered", key: `block-${groupId}-${block.status}` },
            React.createElement("div", { className: "bi-mb-offered-label" },
                "Du har tilbudt",
                block.status === BPSwapOfferStatus.pending
                    ? null
                    : React.createElement(React.Fragment, null,
                        " ",
                        React.createElement("span", { className: "bi-mb-badge" }, swapOfferStatusText(block.status)))),
            block.rows.map((row) => (React.createElement("div", { className: "bi-mb-offered-row", key: `mc-${row.id}` },
                React.createElement("span", { className: "bi-mb-pick-text" },
                    React.createElement("span", { className: "bi-mb-pick-when" }, shiftWhen(row.shift)),
                    React.createElement("span", { className: "bi-mb-pick-role" }, roleName(row.role))),
                block.status === BPSwapOfferStatus.pending
                    ? React.createElement("button", { type: "button", className: "bi-mb-withdraw", disabled: this.state.busy, title: "Tr\u00E6k denne vagt tilbage fra dit bytteforslag", onClick: () => this.withdrawShift(row.offerId, row.shiftMemberId) }, "Tr\u00E6k tilbage")
                    : null)))));
        this.renderGroup = (group) => {
            var _a, _b;
            const se = group.shiftExchange;
            const pending = group.offers.some((offer) => offer.status === BPSwapOfferStatus.pending);
            return (React.createElement("article", { className: "bi-mb-card", key: `group-${group.id}` },
                React.createElement("div", { className: "bi-mb-head" },
                    React.createElement("span", { className: "bi-mb-head-text" },
                        React.createElement("a", { href: `/vagtinfo?shiftId=${(_a = se === null || se === void 0 ? void 0 : se.shift) === null || _a === void 0 ? void 0 : _a.id}` }, shiftWhen(se === null || se === void 0 ? void 0 : se.shift)),
                        React.createElement("span", null,
                            possessive((_b = se === null || se === void 0 ? void 0 : se.offeredByName) !== null && _b !== void 0 ? _b : ""),
                            " vagt \u00B7 ",
                            roleName(se === null || se === void 0 ? void 0 : se.role))),
                    pending
                        ? React.createElement("a", { className: "bi-mb-icon", href: (se === null || se === void 0 ? void 0 : se.exchangeUrl) || `/byttevagt/?slug=${se === null || se === void 0 ? void 0 : se.slug}`, title: "Ret dit bytteforslag", "aria-label": "Ret dit bytteforslag" },
                            React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" },
                                React.createElement("path", { d: "M4 20h4L19 9l-4-4L4 16z" }),
                                React.createElement("path", { d: "m13.5 6.5 4 4" })))
                        : null),
                this.byStatus(group.offers).map((block) => this.renderStatusBlock(block, group.id))));
        };
        this.state = {
            isLoaded: false,
            offers: [],
            showResolved: false,
            busy: false,
            error: null
        };
    }
    componentDidMount() {
        this.load();
        window.addEventListener(SWAP_CHANGED_EVENT, this.load);
    }
    componentWillUnmount() {
        window.removeEventListener(SWAP_CHANGED_EVENT, this.load);
    }
    render() {
        if (!this.state.isLoaded) {
            return (React.createElement("div", { className: "bp-loading" },
                React.createElement("i", { className: "fa fa-spinner fa-pulse fa-fw" })));
        }
        return (React.createElement("section", { className: "bi-mb-section" },
            React.createElement("div", { className: "bi-mb-section-head" },
                React.createElement("h2", null, "Dine bytteforslag"),
                React.createElement("span", null, "Bytteforslag, du har sendt p\u00E5 andres byttevagter.")),
            this.state.error ? React.createElement("div", { className: "bi-bv-alert bi-bv-alert-error", role: "alert" }, this.state.error) : null,
            this.state.offers.length > 0
                ? this.groupByExchange(this.state.offers).map(this.renderGroup)
                : React.createElement("p", { className: "bi-mb-empty" }, "Du har ikke sendt nogen bytteforslag.")));
    }
}
ReactRenderer.render("myshiftofferscomponent", (elem) => React.createElement(MyShiftOffersComponent, Object.assign({}, (elem.dataset))));
